import { PropsWithChildren, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import "./ActionsPopup.scss";
// import { Close } from "@/icons";

export type ActionsPopupProps = PropsWithChildren<{
  isOpen: boolean;
  onClickCloseBtn?: () => void;
  className?: string;
  actions?: React.ReactNode[];
  parentRef?: React.RefObject<HTMLElement>; // Reference to the parent element
}>;

export const ActionsPopup = ({
  isOpen = false,
  onClickCloseBtn,
  className = "",
  actions = [],
  children,
  parentRef,
}: ActionsPopupProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState({
    top: "0.4rem",
    left: "-17rem",
    right: "auto",
  });

  // Function to calculate and adjust position
  const adjustPosition = () => {
    if (!dialogRef.current) return;

    const dialogRect = dialogRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let newPosition = { ...position };

    // Check if the parent reference is provided
    if (parentRef?.current) {
      const parentRect = parentRef.current.getBoundingClientRect();

      // Start with default position relative to parent
      newPosition = {
        top: "1rem",
        left: "-2rem",
        right: "auto",
      };

      // Check if popup extends beyond right edge of viewport
      if (parentRect.right + dialogRect.width - 30 > viewportWidth) {
        // Adjust to open to the left instead
        newPosition = {
          top: "1rem",
          left: "auto",
          right: "0",
        };
      }

      // Check if popup extends beyond bottom edge of viewport
      if (parentRect.bottom + dialogRect.height > viewportHeight) {
        // Adjust to open upward instead
        newPosition.top = `-${dialogRect.height}px`;
      }
    } else {
      // Fallback positioning if no parent reference
      if (dialogRect.right > viewportWidth) {
        newPosition.left = "auto";
        newPosition.right = "0";
      }

      if (dialogRect.bottom > viewportHeight) {
        newPosition.top = `-${dialogRect.height}px`;
      }
    }

    setPosition(newPosition);
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClickCloseBtn?.();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dialogRef.current &&
        !dialogRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        onClickCloseBtn?.();
      }
    };

    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      dialogRef.current?.focus();
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);

      // Adjust position when opened
      adjustPosition();

      // Also adjust on resize
      window.addEventListener("resize", adjustPosition);
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus();
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", adjustPosition);
    };
  }, [isOpen, onClickCloseBtn]);

  if (!isOpen) return null;

  return (
    <div
      className={clsx(
        "popupContainerActions",
        isOpen ? "open" : "close",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{ position: "absolute", right: 0 }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        className={clsx("dialogActions")}
        tabIndex={-1}
        style={{
          top: position.top,
          left: position.left,
          right: position.right,
        }}
      >
        <div className="contentActions">
          {/*<button*/}
          {/*  onClick={(e) => {*/}
          {/*    e.stopPropagation();*/}
          {/*    onClickCloseBtn?.();*/}
          {/*  }}*/}
          {/*  className="closeButtonActions"*/}
          {/*  aria-label="Chiudi popup"*/}
          {/*>*/}
          {/*  <Close className="closeIcon" />*/}
          {/*</button>*/}

          <ul className="actionsList">
            {actions.map((action, index) => (
              <li key={index} className="actionsListItem">
                {action}
              </li>
            ))}
          </ul>

          {children}
        </div>
      </div>
    </div>
  );
};
